import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AuditLog {
  id: string;
  entity_type: string;
  entity_id: string;
  entity_name: string;
  action_type: string;
  field_changed: string;
  old_value: string;
  new_value: string;
  metadata: any;
  changed_by: string;
  changed_by_name: string;
  user_email: string;
  created_at: string;
  total_count: number;
}

interface Filters {
  entityType?: string;
  entityId?: string;
  changedBy?: string;
  actionType?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export function useAuditLogs(filters: Filters = {}) {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function fetchLogs(page = 0, limit = 50) {
    try {
      setLoading(true);
      setError(null);

      const { data, error: err } = await supabase.rpc('get_audit_logs' as any, {
        p_entity_type: filters.entityType || null,
        p_entity_id: filters.entityId || null,
        p_changed_by: filters.changedBy || null,
        p_action_type: filters.actionType || null,
        p_date_from: filters.dateFrom || null,
        p_date_to: filters.dateTo || null,
        p_search: filters.search || null,
        p_limit: limit,
        p_offset: page * limit
      });

      if (err) throw err;

      if (data && Array.isArray(data) && data.length > 0) {
        setLogs(data as AuditLog[]);
        setTotalCount((data as any)[0]?.total_count || 0);
      } else {
        setLogs([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      console.error('Error fetching audit logs:', err);
      setError(err.message);
      setLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchLogs();
  }, [
    filters.entityType,
    filters.entityId,
    filters.changedBy,
    filters.actionType,
    filters.dateFrom,
    filters.dateTo,
    filters.search
  ]);

  return {
    logs,
    loading,
    error,
    totalCount,
    fetchLogs,
    refetch: () => fetchLogs()
  };
}
